// Shared customer-identity guards for the Business Web plane.
//
// Incident (2026-07-31): the /business/customers detail rendered a raw internal identifier
// (`user-<uid>`) as "Primary Phone", and names could show "Unknown". Normal Business UI (and exports,
// chart labels, tooltips, drill-downs) must NEVER expose a raw internal identifier as friendly customer
// data. This module is the single source of truth for that rule so every customer surface behaves the same.
//
// Rule: reject raw internal identifiers (user-*, cust_*, staff_*, biz_*, UUIDs, Firebase-style uids) from
// name / phone / email, validate phone and email shape, and provide honest fallbacks.

// Leading-pattern match for the common synthetic/internal id prefixes + UUID.
const RAW_ID_RE = /^(user[-_]|cust[-_]|staff[-_]|biz[-_]|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;
// A Firebase-style uid is a long opaque alphanumeric token with no separators — not a friendly value.
const FIREBASE_UID_RE = /^[A-Za-z0-9]{20,}$/;

export function isRawInternalId(v) {
  if (typeof v !== 'string') return false;
  const s = v.trim();
  if (!s) return false;
  return RAW_ID_RE.test(s) || (FIREBASE_UID_RE.test(s) && !/\s/.test(s));
}

/** A validated, display-safe phone or null. Rejects raw ids and anything that isn't phone-shaped. */
export function safePhone(v) {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  if (!s || isRawInternalId(s)) return null;
  // phone-shaped: digits with optional + ( ) - and spaces, at least 6 chars, must contain a digit.
  return /^[+()\-\s0-9]{6,}$/.test(s) && /[0-9]/.test(s) ? s : null;
}

/** A validated email or null. */
export function safeEmail(v) {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  if (!s || isRawInternalId(s)) return null;
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s) ? s : null;
}

/**
 * A friendly customer name, or null when only a raw id / "Unknown" is available.
 * Accepts a customer-like object with name/display_name/full_name/first_name/last_name.
 */
export function safeCustomerName(c) {
  if (!c || typeof c !== 'object') return null;
  const candidates = [
    c.name,
    c.display_name,
    c.full_name,
    `${c.first_name || ''} ${c.last_name || ''}`.trim(),
  ];
  for (const raw of candidates) {
    const s = (raw || '').trim();
    if (s && !/^unknown$/i.test(s) && !isRawInternalId(s)) return s;
  }
  return null;
}

/**
 * A display-safe staff/operator/provider name, or null when only a raw id / uid fragment is available.
 * Accepts a plain name string OR a staff-like object (name/display_name/full_name/first+last).
 * D3: replaces the ad-hoc `staff_id.slice(0,8)` fallbacks so a staff surface NEVER shows a uid fragment.
 */
export function safeStaffName(nameOrObj) {
  let s = '';
  if (typeof nameOrObj === 'string') s = nameOrObj.trim();
  else if (nameOrObj && typeof nameOrObj === 'object') {
    s = (nameOrObj.name || nameOrObj.display_name || nameOrObj.full_name
      || `${nameOrObj.first_name || ''} ${nameOrObj.last_name || ''}`.trim() || '').trim();
  }
  if (s && !/^unknown$/i.test(s) && !isRawInternalId(s)) return s;
  return null;
}

// Display fallbacks (never a raw id).
export const NAME_FALLBACK = 'Customer';        // friendly generic (matches the mobile apps)
export const STAFF_FALLBACK = 'Staff member';   // friendly generic for operators/providers
export const NOT_PROVIDED = 'Not provided';

/** Display helpers for UI render sites. */
export const displayCustomerName = (c) => safeCustomerName(c) || NAME_FALLBACK;
export const displayStaffName = (nameOrObj, fallback = STAFF_FALLBACK) => safeStaffName(nameOrObj) || fallback;
export const displayPhone = (v) => safePhone(v) || NOT_PROVIDED;
export const displayEmail = (v) => safeEmail(v) || NOT_PROVIDED;
